import { ChangeDetectorRef, Component, EnvironmentInjector, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ApiClientService } from '@services/api-client';
import { GetUserResponse } from '@services/api-client/responses/get-user-response.type';
import { AuthenticationService } from '@services/authentication/authentication.service';
import { CachedValuesService } from '@services/cached-values/cached-values.service';
import { ToastService } from '@services/toast/toast.service';
import { UserForm } from '../../components/create-and-update-user-modal/create-and-update-user-modal.model';
import { matchValidator, passwordValidator, RoutesHelper } from '@helpers/index';
import { take } from 'rxjs';
import { DropdownItem } from '@shared/components/dropdown/dropdown.component';
import { Role, ValidationMessageKey } from 'app/enums';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss'],
    standalone: false
})
export class UserProfileComponent implements OnInit {
    private readonly apiClientService = inject(ApiClientService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly injector = inject(EnvironmentInjector);
    private readonly cachedValuesService = inject(CachedValuesService);
    public showHeader = false;

    public showPotentialValidationMessages = true;
    public userForm: FormGroup<UserForm>;
    public subscriptions: DropdownItem<number>[];
    public selectedSubscriptionPlan: number;
    public editMode = false;
    public editPassword = false;
    public editSubscriptionMode = false;

    public get f() {
        return this.userForm.controls;
    }

    public ngOnInit(): void {
        const fb = new FormBuilder();
        this.userForm = fb.nonNullable.group({
            firstName: ['', [Validators.required]],
            lastName: ['', [Validators.required]],
            email: ['', [Validators.required, Validators.email]],
            phone: [''],
            password: [''],
            confirmPassword: [''],
            createPrivateTenant: [false],
            roles: [[] as Role[]]
        });
        const userId = this.authenticationService.getUser().userId;
        this.apiClientService.getUser$(userId).subscribe({
            next: (response: GetUserResponse) => {
                if (response) {
                    this.userForm = fb.nonNullable.group({
                        firstName: [response?.firstName, [Validators.required]],
                        lastName: [response?.lastName, [Validators.required]],
                        email: [response?.email, [Validators.required, Validators.email]],
                        phone: [response?.phone],
                        password: [''],
                        confirmPassword: [''],
                        createPrivateTenant: [false],
                        roles: [response?.roles ?? []]
                    });

                    const setPasswordValidators = () => {
                        const password = this.f.password.value;
                        const confirmPassword = this.f.confirmPassword.value;
                        const shouldValidate = password || confirmPassword;

                        if (shouldValidate) {
                            this.userForm.setValidators([
                                matchValidator('password', 'confirmPassword', ValidationMessageKey.PasswordsMismatch),
                                passwordValidator('password', ValidationMessageKey.WeakPassword)
                            ]);
                            this.f.password.setValidators([Validators.required]);
                            this.f.confirmPassword.setValidators([Validators.required]);
                        } else {
                            this.userForm.clearValidators();
                            this.f.password.clearValidators();
                            this.f.confirmPassword.clearValidators();
                        }
                        this.f.password.updateValueAndValidity({ emitEvent: false });
                        this.f.confirmPassword.updateValueAndValidity({ emitEvent: false });
                        this.userForm.updateValueAndValidity({ emitEvent: false });
                    };

                    // Listen for changes to password fields
                    this.f.password.valueChanges.subscribe(() => setPasswordValidators());
                    this.f.confirmPassword.valueChanges.subscribe(() => setPasswordValidators());

                    setPasswordValidators();
                }
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant('FEATURES.USER.TOAST_GET_ERROR.MESSAGE') as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            }
        });
    }

    public saveUser() {
        if (this.userForm.invalid) {
            this.showPotentialValidationMessages = true;
            this.changeDetectorRef.markForCheck();
            return;
        }

        this.apiClientService
            .updateUser$({
                userId: this.authenticationService.getUser().userId,
                firstName: this.f.firstName.value,
                lastName: this.f.lastName.value,
                email: this.f.email.value,
                phone: this.f.phone.value,
                setPassword: this.f.password.value,
                roles: this.f.roles.value
            })
            .pipe(take(1))
            .subscribe({
                next: () => {
                    const title = this.translateService.instant('TOAST_TITLES.SUCCESS') as string;
                    const message = this.translateService.instant(
                        'FEATURES.USER.TOAST_MESSAGES.UPDATE_USER_SUCCESS'
                    ) as string;
                    this.cachedValuesService.invalidateCache();
                    this.toastService.open(title, message, { type: 'success' });
                    this.editMode = false;
                    this.changeDetectorRef.markForCheck();
                },
                error: () => {
                    this.changeDetectorRef.markForCheck();
                    const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                    const message = this.translateService.instant(
                        'FEATURES.USER.TOAST_MESSAGES.UPDATE_USER_ERROR'
                    ) as string;
                    this.toastService.open(title, message, { type: 'error', progressBar: true });
                }
            });
    }

    public savePassword() {
        if (this.userForm.invalid) {
            this.showPotentialValidationMessages = true;
            this.changeDetectorRef.markForCheck();
            return;
        }
    }

    public logout() {
        this.injector.runInContext(() => {
            RoutesHelper.RunInContext.navigateToLoginAndTriggerLogout();
        });
    }

    public enterEditMode() {
        this.editMode = true;
    }
    public exitEditMode() {
        this.editMode = false;
    }
    public enterEditPassword() {
        this.editPassword = true;
    }
    public exitEditPassword() {
        this.editPassword = false;
    }
}
